import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Result,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import {
  fetchWorkspace,
  fetchWorkspaceMembershipsForUser,
  respondToWorkspaceInvitation,
} from "services/workspaceService";
import CopyableText from "common/CopyableText";
import paths from "routes/paths";
import {
  formatDateTime,
  membershipPrivilegeNames,
  membershipRoleNames,
  membershipUserEmail,
  membershipUserName,
  readable,
  statusColor,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceStyles } from "./colors";

const { Text, Title } = Typography;

const isPending = (invitation) =>
  String(invitation?.membershipStatus || "").toUpperCase() === "PENDING";

const WorkspaceInvitationAcceptPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { refreshWorkspaces, selectWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const workspaceId = params.workspaceId || searchParams.get("workspaceId") || "";
  const invitationId = params.invitationId || searchParams.get("invitationId") || "";
  const [workspace, setWorkspace] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState("");
  const [completedAction, setCompletedAction] = useState("");
  const [loadError, setLoadError] = useState("");

  const title = useMemo(() => {
    if (workspace?.name) return workspace.name;
    return workspaceId ? "Workspace invitation" : "Invitation";
  }, [workspace?.name, workspaceId]);
  const roleNames = useMemo(() => membershipRoleNames(invitation), [invitation]);
  const privilegeNames = useMemo(() => membershipPrivilegeNames(invitation), [invitation]);

  const loadInvitation = useCallback(async () => {
    if (!actorId || !workspaceId) return;

    try {
      setLoading(true);
      setLoadError("");

      const [workspaceResult, membershipsResult] = await Promise.allSettled([
        fetchWorkspace(workspaceId, actorId),
        fetchWorkspaceMembershipsForUser({
          actorId,
          userId: actorId,
          size: 100,
        }),
      ]);

      if (workspaceResult.status === "fulfilled") {
        setWorkspace(workspaceResult.value || null);
      } else {
        setWorkspace({ id: workspaceId, name: "Workspace" });
      }

      if (membershipsResult.status === "rejected") {
        throw membershipsResult.reason;
      }

      const memberships = membershipsResult.value?.content || [];
      const matchedInvitation =
        memberships.find((item) => invitationId && item?.id === invitationId) ||
        memberships.find((item) => item?.workspaceId === workspaceId && isPending(item)) ||
        memberships.find((item) => item?.workspaceId === workspaceId) ||
        null;

      setInvitation(matchedInvitation);
    } catch (error) {
      setLoadError(error?.message || "Failed to load workspace invitation.");
    } finally {
      setLoading(false);
    }
  }, [actorId, invitationId, workspaceId]);

  useEffect(() => {
    loadInvitation();
  }, [loadInvitation]);

  const respond = async (action) => {
    if (!workspaceId || processingAction) return;

    try {
      setProcessingAction(action);
      await respondToWorkspaceInvitation({
        actorId,
        workspaceId,
        action,
      });
      setCompletedAction(action);
      message.success(`Invitation ${action === "accept" ? "accepted" : "declined"}.`);
      if (action === "accept") {
        await refreshWorkspaces();
        selectWorkspace(workspaceId);
      }
      await loadInvitation();
    } catch (error) {
      message.error(error?.message || `Failed to ${action} invitation.`);
    } finally {
      setProcessingAction("");
    }
  };

  if (!workspaceId) {
    return (
      <div className="project-page">
        <Result
          status="warning"
          title="Invitation link is missing workspace information"
          extra={<Button onClick={() => navigate(paths.workspaces)}>Open Workspaces</Button>}
        />
      </div>
    );
  }

  return (
    <div className="project-page">
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-2 flex-wrap">
          <div>
            <Title level={3} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
              Workspace Invitation
            </Title>
            <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
              Review and respond to this workspace invitation.
            </Text>
          </div>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.workspaces)}>
            Workspaces
          </Button>
        </div>

        {loading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        ) : loadError ? (
          <Alert type="error" showIcon message={loadError} />
        ) : !invitation ? (
          <Card>
            <Empty
              image={<MailOutlined style={{ fontSize: 42, color: OLIVEEB_COLORS.oliveebBlue }} />}
              description="No matching invitation was found for your account"
            >
              <Button onClick={() => navigate(paths.workspaces)}>Review invitations</Button>
            </Empty>
          </Card>
        ) : (
          <Card
            style={{
              borderRadius: 8,
              borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
              boxShadow: "0 14px 34px rgba(0, 64, 56, 0.08)",
            }}
          >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <div className="d-flex align-items-start gap-3">
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 8,
                    display: "grid",
                    placeItems: "center",
                    background: workspaceStyles.iconBubble.background,
                    color: workspaceStyles.iconBubble.color,
                    flex: "0 0 auto",
                    fontSize: 20,
                  }}
                >
                  <MailOutlined />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    {title}
                  </Title>
                  <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
                    <Tag color={statusColor(invitation.membershipStatus)}>
                      {readable(invitation.membershipStatus, "Pending")}
                    </Tag>
                    {roleNames.map((role) => (
                      <Tag key={role} color="blue">
                        {readable(role)}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </div>

              {completedAction ? (
                <Alert
                  type={completedAction === "accept" ? "success" : "info"}
                  showIcon
                  message={`Invitation ${completedAction === "accept" ? "accepted" : "declined"}.`}
                />
              ) : null}

              <Descriptions
                bordered
                size="small"
                column={{ xs: 1, md: 2 }}
                items={[
                  {
                    key: "workspaceId",
                    label: "Workspace ID",
                    children: <CopyableText value={workspaceId} />,
                  },
                  {
                    key: "invitationId",
                    label: "Invitation ID",
                    children: invitationId || invitation.id ? (
                      <CopyableText value={invitationId || invitation.id} />
                    ) : (
                      "-"
                    ),
                  },
                  {
                    key: "member",
                    label: "Invited member",
                    children:
                      [membershipUserName(invitation), membershipUserEmail(invitation)]
                        .filter(Boolean)
                        .join(" • ") || "-",
                  },
                  {
                    key: "sent",
                    label: "Sent",
                    children: formatDateTime(invitation.createdAt || invitation.sentAt) || "-",
                  },
                  {
                    key: "roles",
                    label: "Roles",
                    children: roleNames.length ? (
                      <Space wrap size={[4, 4]}>
                        {roleNames.map((role) => (
                          <Tag key={role} color="blue">{readable(role)}</Tag>
                        ))}
                      </Space>
                    ) : "-",
                  },
                  {
                    key: "privileges",
                    label: "Privileges",
                    children: privilegeNames.length ? (
                      <Space wrap size={[4, 4]}>
                        {privilegeNames.map((privilege) => (
                          <Tag key={privilege} color="green">{readable(privilege)}</Tag>
                        ))}
                      </Space>
                    ) : "-",
                  },
                ]}
              />

              {isPending(invitation) ? (
                <Space wrap>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    loading={processingAction === "decline"}
                    disabled={Boolean(processingAction)}
                    onClick={() => respond("decline")}
                  >
                    Decline
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={processingAction === "accept"}
                    disabled={Boolean(processingAction)}
                    onClick={() => respond("accept")}
                  >
                    Accept invitation
                  </Button>
                </Space>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message={`This invitation is ${readable(invitation.membershipStatus, "not pending").toLowerCase()}.`}
                />
              )}
            </Space>
          </Card>
        )}
      </Space>
    </div>
  );
};

export default WorkspaceInvitationAcceptPage;
